<template>
  <div
    class="relative flex items-start min-h-screen w-full max-w-full overflow-x-clip bg-slate-50 dark:bg-slate-900"
  >
    <!-- Sidebar -->
    <AdminSidebar
      :is-collapsed="isSidebarCollapsed"
      :is-open-mobile="isSidebarOpenMobile"
      :sidebar-search="sidebarSearch"
      :lang="lang"
      :t="t"
      @update:sidebar-search="sidebarSearch = $event"
      @update:is-open-mobile="isSidebarOpenMobile = $event"
    />


    <!-- Main content -->
    <div class="flex flex-col flex-1 min-w-0 w-full">
      <!-- Top bar -->
      <AdminTopbar
        :is-collapsed="isSidebarCollapsed"
        :lang="lang"
        :t="t"
        @toggle-sidebar="toggleSidebar"
      />

      <!-- Content -->
      <main class="flex-1 px-4 py-6 md:px-6 lg:px-8">
        <div class="max-w-6xl mx-auto space-y-6">
          <!-- Header -->
          <div
            class="flex flex-col justify-between gap-3 sm:flex-row sm:items-center"
          >
            <div>
              <h1 class="text-lg font-semibold text-slate-900 dark:text-white">
                {{ t("profile_title") }}
              </h1>
              <p class="text-sm text-slate-500 dark:text-slate-400">
                {{ t("profile_subtitle") }}
              </p>
            </div>
          </div>

          <!-- Loading -->
          <section
            v-if="loading"
            class="p-6 bg-white border shadow-sm dark:bg-slate-900 border-slate-200 dark:border-slate-700 rounded-2xl"
          >
            <div class="space-y-3 animate-pulse">
              <div
                class="w-40 h-5 rounded bg-slate-200 dark:bg-slate-700"
              ></div>
              <div
                class="w-64 h-3 rounded bg-slate-200 dark:bg-slate-700"
              ></div>
              <div class="h-24 bg-slate-200 dark:bg-slate-700 rounded-xl"></div>
            </div>
          </section>

          <!-- Empty -->
          <section
            v-else-if="!user"
            class="p-6 text-center bg-white border shadow-sm dark:bg-slate-900 border-slate-200 dark:border-slate-700 rounded-2xl"
          >
            <i class="text-3xl mdi mdi-account-off-outline text-slate-400"></i>
            <p class="mt-2 text-sm text-slate-600 dark:text-slate-300">
              {{ t("cookie_err_user_data_not_found") }}
            </p>
          </section>

          <!-- Profile Header -->
          <section
            v-else
            class="p-4 bg-white border shadow-sm sm:p-5 dark:bg-slate-900 border-slate-200 dark:border-slate-700 rounded-2xl"
          >
            <div
              class="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between"
            >
              <div class="flex items-center gap-4">
                <!-- Avatar -->
                <div
                  class="flex items-center justify-center w-16 h-16 overflow-hidden text-xl font-bold text-white shadow-sm rounded-2xl bg-gradient-to-br from-primary to-secondary"
                >
                  <img
                    v-if="avatarSrc"
                    :src="avatarSrc"
                    class="object-cover w-full h-full"
                    :alt="user?.name"
                  />
                  <span v-else>{{ initials }}</span>
                </div>

                <!-- Name / role -->
                <div>
                  <p class="text-base font-bold text-slate-900 dark:text-white">
                    {{ user?.name || "-" }}
                  </p>

                  <!-- ✅ translated role + icon -->
                  <p
                    class="flex items-center gap-1 text-xs capitalize text-slate-500 dark:text-slate-400"
                  >
                    <i class="mdi text-[16px]" :class="roleIcon"></i>
                    <span>{{ roleLabel || "-" }}</span>
                  </p>

                  <div class="flex flex-wrap gap-2 mt-2 text-xs">
                    <span
                      class="inline-flex items-center gap-1 px-2 py-1 rounded-full bg-primary/10 text-primary"
                    >
                      <i class="mdi mdi-email-outline"></i>
                      {{ user?.email || "-" }}
                    </span>
                    <span
                      class="inline-flex items-center gap-1 px-2 py-1 rounded-full bg-secondary/10 text-secondary"
                    >
                      <i class="mdi mdi-phone-outline"></i>
                      {{ user?.phone || "-" }}
                    </span>
                  </div>
                </div>
              </div>

              <!-- Status -->
              <div>
                <span
                  class="inline-flex items-center gap-1 px-3 py-1 text-xs font-semibold rounded-full"
                  :class="
                    user?.status === 1
                      ? 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/40 dark:text-emerald-300'
                      : 'bg-slate-100 text-slate-700 dark:bg-slate-800 dark:text-slate-300'
                  "
                >
                  <i class="mdi mdi-circle-small"></i>
                  {{
                    user?.status === 1
                      ? t("status_active")
                      : t("status_inactive")
                  }}
                </span>
              </div>
            </div>
          </section>

          <!-- Sections -->
          <div v-if="user" class="grid grid-cols-1 gap-6 lg:grid-cols-2">
            <!-- ABOUT - CONSULTANT -->
            <CardSection
              v-if="isConsultant"
              :title="t('section_about_consultant')"
              icon="mdi mdi-account-tie-outline"
            >
              <ProfileRow
                :label="t('field_consultant_name')"
                :value="user?.consultant?.name || user?.name || '-'"
              />
              <ProfileRow
                :label="t('field_license_number')"
                :value="user?.consultant?.license_number || '-'"
              />

              <!-- ✅ consultant type translated -->
              <ProfileRow
                :label="t('field_type')"
                :value="consultantTypeLabel || '-'"
              />

              <ProfileRow
                :label="t('field_specialization')"
                :value="user?.consultant?.specialization || '-'"
              />
              <ProfileRow
                :label="t('field_qualification')"
                :value="user?.consultant?.qualification || '-'"
              />
            </CardSection>

            <!-- ABOUT - CLIENT -->
            <CardSection
              v-if="isClient"
              :title="t('section_about_client')"
              icon="mdi mdi-briefcase-outline"
            >
              <ProfileRow
                :label="t('field_client')"
                :value="user?.client?.name"
              />
              <ProfileRow
                :label="t('field_manager')"
                :value="user?.client?.manager_name"
              />

              <!-- ✅ client type translated -->
              <ProfileRow
                :label="t('field_client_type')"
                :value="clientTypeLabel || '-'"
              />

              <ProfileRow
                :label="t('field_activity')"
                :value="user?.client?.activity"
              />
              <ProfileRow
                :label="t('simple_description')"
                :value="
                  user?.client?.activity_description ||
                  user?.activity_description ||
                  '-'
                "
              />
            </CardSection>

            <!-- USER -->
            <CardSection
              :title="t('section_user')"
              icon="mdi mdi-account-outline"
            >
              <ProfileRow :label="t('field_user_name')" :value="user?.name" />
              <ProfileRow :label="t('field_email')" :value="user?.email" />
              <ProfileRow :label="t('field_phone')" :value="user?.phone" />
              <ProfileRow
                :label="t('field_status')"
                :value="
                  user?.status === 1 ? t('status_active') : t('status_inactive')
                "
              />

              <!-- ✅ roleLabel -->
              <ProfileRow :label="t('field_role')" :value="roleLabel || '-'" />
            </CardSection>

            <!-- ADDRESS -->
            <CardSection
              v-if="hasAddressSection"
              :title="t('section_address')"
              icon="mdi mdi-map-marker-outline"
            >
              <ProfileRow
                :label="t('field_city')"
                :value="user?.address?.city"
              />
              <ProfileRow
                :label="t('field_district')"
                :value="user?.address?.district"
              />
              <ProfileRow
                :label="t('field_street')"
                :value="user?.address?.street"
              />
              <ProfileRow
                :label="t('field_building_no')"
                :value="user?.address?.building_number"
              />
              <ProfileRow
                :label="t('field_additional_no')"
                :value="user?.address?.additional_number"
              />
              <ProfileRow
                :label="t('field_postal_code')"
                :value="user?.address?.postal_code"
              />
              <ProfileRow
                :label="t('field_short_address')"
                :value="user?.address?.short_address"
              />
            </CardSection>

            <!-- OVERVIEW - BANK -->
            <CardSection
              v-if="hasBankSection"
              :title="t('section_bank')"
              icon="mdi mdi-bank-outline"
            >
              <ProfileRow
                :label="t('field_account_holder')"
                :value="user?.bank_account?.account_holder_name"
              />
              <ProfileRow
                :label="t('field_account_no')"
                :value="user?.bank_account?.account_number"
              />
              <ProfileRow
                :label="t('field_bank_name')"
                :value="user?.bank_account?.bank_name"
              />
            </CardSection>

            <!-- OFFICE -->
            <CardSection
              v-if="hasOfficeSection && isClient"
              class="lg:col-span-2"
              :title="t('section_office')"
              icon="mdi mdi-office-building-outline"
            >
              <div class="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
                <ProfileRow
                  :label="t('field_commercial_reg')"
                  :value="user?.office?.commercial_registration_number"
                />
                <ProfileRow
                  :label="t('field_establishment_no')"
                  :value="user?.office?.establishment_number"
                />
                <ProfileRow
                  :label="t('field_unified_no')"
                  :value="user?.office?.unified_number"
                />
                <ProfileRow
                  :label="t('field_tax_no')"
                  :value="user?.office?.tax_number"
                />
                <ProfileRow
                  :label="t('field_insurance_no')"
                  :value="user?.office?.insurance_number"
                />
              </div>

              <div class="mt-6">
                <h3
                  class="mb-3 text-xs font-bold tracking-wide text-slate-600 dark:text-slate-300"
                >
                  {{ t("section_documents") }}
                </h3>

                <div
                  class="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3"
                >
                  <DocCard label="Logo" :url="user?.office?.logo" />
                  <DocCard label="Letterhead" :url="user?.office?.letterhead" />
                  <DocCard
                    label="Unified Doc"
                    :url="user?.office?.unified_doc"
                  />
                  <DocCard
                    label="Establishment Doc"
                    :url="user?.office?.establishment_doc"
                  />
                  <DocCard
                    label="Registration Doc"
                    :url="user?.office?.registeration_doc"
                  />
                </div>
              </div>
            </CardSection>
          </div>
        </div>
      </main>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, onMounted, onBeforeUnmount } from "vue";
import { messages } from "@/i18n";
import { useRoleLabel } from "@/composables/useRoleLabel";
import {
  useClientTypeLabel,
  useClientTypeIcon,
  useConsultantTypeLabel,
  useConsultantTypeIcon,
} from "@/composables/useAccountTypeLabels";

import AdminSidebar from "@/components/Auth/Sidebar.vue";
import AdminTopbar from "@/components/Auth/TopNav.vue";
import CardSection from "@/components/Auth/CardSection.vue";
import ProfileRow from "@/components/Auth/ProfileRow.vue";
import DocCard from "@/components/Auth/DocCard.vue";

// ----- lang -----
const initialLang = (
  document.documentElement.lang ||
  localStorage.getItem("app_lang") ||
  "ar"
).toLowerCase();

const lang = ref(initialLang.startsWith("ar") ? "ar" : "en");

// ----- state -----
const isSidebarCollapsed = ref(false);
const isSidebarOpenMobile = ref(false);
const sidebarSearch = ref("");
const user = ref(null);
const loading = ref(true);

let _langObserver = null;
let _langHandler = null;

// ----- i18n helper -----
function t(key) {
  return (messages?.[lang.value] && messages[lang.value][key]) || key;
}

// ----- sidebar toggle -----
function toggleSidebar() {
  if (window.innerWidth < 768)
    isSidebarOpenMobile.value = !isSidebarOpenMobile.value;
  else isSidebarCollapsed.value = !isSidebarCollapsed.value;
}

// ----- cookies -----
function getCookie(name) {
  const match = document.cookie.match(
    new RegExp(
      "(?:^|; )" + name.replace(/([.$?*|{}()[\]\\/+^])/g, "\\$1") + "=([^;]*)"
    )
  );
  return match ? decodeURIComponent(match[1]) : null;
}

function loadUserFromCookie() {
  loading.value = true;
  const raw = getCookie("auth_user");

  if (!raw) {
    user.value = null;
    loading.value = false;
    return;
  }

  try {
    let parsed = JSON.parse(raw);
    if (typeof parsed === "string") parsed = JSON.parse(parsed);

    const root = parsed?.data ?? parsed;
    user.value = root?.user ?? root;
  } catch (e) {
    console.error("Failed to parse auth_user cookie", e);
    user.value = null;
  } finally {
    loading.value = false;
  }
}

// ----- role label + icon -----
const roleSource = computed(() => user.value?.role || user.value?.type || "");
const roleLabel = useRoleLabel(roleSource, lang);

const roleIcon = computed(() => {
  const r = String(roleSource.value || "").toLowerCase();
  if (r.includes("super") || r.includes("admin"))
    return "mdi-shield-crown-outline";
  if (r.includes("manager")) return "mdi-account-cog-outline";
  if (r.includes("consultant")) return "mdi-account-tie-outline";
  if (r.includes("office")) return "mdi-office-building-outline";
  if (r.includes("client")) return "mdi-account-outline";
  return "mdi-account-badge-outline";
});

// ----- client_type label + icon -----
const clientTypeSource = computed(() => user.value?.client?.client_type || "");
const clientTypeLabel = useClientTypeLabel(clientTypeSource, lang, {
  fallback: "-",
});
const clientTypeIcon = useClientTypeIcon(clientTypeSource); // available if you need it

// ----- consultant_type label + icon -----
const consultantTypeSource = computed(
  () =>
    user.value?.consultant?.consultant_type ||
    user.value?.consultant?.type ||
    user.value?.consultant_type ||
    ""
);

const consultantTypeLabel = useConsultantTypeLabel(consultantTypeSource, lang, {
  fallback: "-",
});
const consultantTypeIcon = useConsultantTypeIcon(consultantTypeSource); // available if you need it

// ----- computed helpers -----
const initials = computed(() => {
  const name = user.value?.name || "";
  if (!name) return "U";
  const parts = name.trim().split(" ");
  return ((parts[0]?.[0] || "") + (parts[1]?.[0] || "")).toUpperCase();
});

const avatarSrc = computed(
  () => user.value?.avatar || user.value?.office?.logo || ""
);

// ✅ isConsultant / isClient
const isConsultant = computed(() => {
  const r = String(roleSource.value || "").toLowerCase();
  return r.includes("consultant") || r.includes("مستشار");
});
const isClient = computed(() => !isConsultant.value);

// sections existence
const hasAddressSection = computed(() => !!user.value?.address);
const hasBankSection = computed(() => !!user.value?.bank_account);
const hasOfficeSection = computed(() => !!user.value?.office);

// ----- lang sync -----
function syncLangListeners() {
  _langHandler = (e) => {
    const next = String(e.detail || "ar")
      .toLowerCase()
      .startsWith("en")
      ? "en"
      : "ar";
    if (next !== lang.value) lang.value = next;
  };
  window.addEventListener("app:lang", _langHandler, { passive: true });

  _langObserver = new MutationObserver(() => {
    const htmlLang = (
      document.documentElement.lang ||
      localStorage.getItem("app_lang") ||
      "ar"
    ).toLowerCase();
    const next = htmlLang.startsWith("en") ? "en" : "ar";
    if (next !== lang.value) lang.value = next;
  });

  _langObserver.observe(document.documentElement, {
    attributes: true,
    attributeFilter: ["lang", "dir"],
  });
}

onMounted(() => {
  syncLangListeners();
  loadUserFromCookie();
});

onBeforeUnmount(() => {
  if (_langObserver) _langObserver.disconnect();
  if (_langHandler) window.removeEventListener("app:lang", _langHandler);
});
</script>
